<script>

    import { onMount, onDestroy, afterUpdate, createEventDispatcher } from 'svelte';
    import { fade } from 'svelte/transition';
    import { get } from 'svelte/store';

    import {
        device_permissions,
        core_init_ok,
        core_init_failed,
        connection_status,
        duplicateEngAlert,
        conf_conn_ok,
        playback_ctx_resume_ok,
        avail_devices,
        selected_devices,
        getSelectedDeviceMuted,
        has_joined } from '../../stores.js';

    import { Peers_Handler } from "../../Peers.js";
    import { Devices } from "../../Devices.js";
    import { OutputDevices } from "../../OutputDevices.js";
    import { UserPrefs } from "../../UserPrefs.js";
    import { Dispatcher } from '../../Dispatcher.js';
    import { Mixer } from '../../Mixer.js';
    import { BuildSettings, OutputConfig } from '../../OutputConfig.js';
    import { BrowserHandler } from '../../BrowserHandler.js';
    import { Conference } from '../../Conference/Conference.js';
    import { DTSInterface } from "../../DTSInterface.js";
    import { Logger } from "../../Logger.js";

    import Loader from './Loader.svelte';
    import CameraPreview from './CameraPreview.svelte';

    export let devicesHidden;
    export let self_type;
    export let conf_disabled;

    const dispatch = createEventDispatcher();

    // -- iOS + Android open the mic with echo cancellation off (see Devices._constraintsFor), so the
    // -- built-in mic picks up the speaker: it gets the headphones notice and joins muted
    $: builtin_mic = $device_permissions.microphone === "granted"
        && Devices.isBuiltInMobileMic($selected_devices.microphone, $avail_devices.microphone);

    let name = "";
    
    let can_join = false;
    let joining = false;
    let joinBtnText = "Loading";

    let loading_media = false;

    let _use_expanded_io = false;

    // -- expanded io must reach OutputDevices: a talent's stream selection is only independent
    // -- of the speaker while this is on (see OutputDevices.streamIndependent). Talent-gated so
    // -- the eng default (set true in Peers.create_self) isn't stomped.
    $: if (self_type === "talent") Peers_Handler.self_peer.use_expanded_io.set(_use_expanded_io);

    $: direct_join = conf_disabled && self_type === "client";
    let _auto_join = false;

    $: _avail_devices = $avail_devices;
    $: _selected_devices = $selected_devices;
    $: _camera_muted = _selected_devices.camera?.muted ? get(_selected_devices.camera.muted) : false;

    let local_video_rendering = OutputConfig.local_video_rendering;
    $: OutputConfig.local_video_rendering = local_video_rendering;

    let video_bitrate = OutputConfig.video_bitrate;
    $: OutputConfig.video_bitrate = video_bitrate;

    const unsub_name = Peers_Handler.self_peer.name.subscribe(n => {
        name = n;
    });

    // -- the mic permission is the floor for joining: without it the person can't be heard.
    // -- No-conf never asks for permissions, so it doesn't gate there.
    $: mic_permitted = conf_disabled || $device_permissions.microphone === "granted";

    // -- conf_conn_ok is the jitsi *connection*, not the join -- joining is what this button does.
    $:  if ($conf_conn_ok && $core_init_ok && $playback_ctx_resume_ok && !loading_media && mic_permitted) _allowJoin();
        else _disallowJoin();

    // -- direct-join: once the audio context has resumed (can_join) after the Join gesture, enter the session.
    $: if (_auto_join && can_join){ _auto_join = false; onJoinClick(); }

    // -- core_init_ok lands within a second or two of the ws connecting. Still missing this long
    // -- after the click = a first boot that died without saying so (ws down: the reconnect loop's job)
    const CORE_INIT_WATCHDOG_MS = 15000;
    let _init_watchdog = null;
    let _init_watch_start = 0;

    function _armInitWatchdog(){

        if (_init_watchdog || get(core_init_ok)) return;
        if (!_init_watch_start) _init_watch_start = performance.now();

        _init_watchdog = setTimeout(() => {
            _init_watchdog = null;
            if (get(core_init_ok) || get(core_init_failed) || get(duplicateEngAlert)) return;

            const status = get(connection_status);
            if (!status.ws && !status.gave_up){ _armInitWatchdog(); return; }

            Logger.log("Session", "warn", "Core Init Timeout", {waited_ms: Math.round(performance.now() - _init_watch_start), ...status});
            core_init_failed.set(true);
        }, CORE_INIT_WATCHDOG_MS);
    }

    function onReloadClick(){
        Logger.log("Page Load", "info", "App Reload", {reason: "core-init-failed"});
        Logger.flush(true);
        window.location.reload();
    }


    function onNameButtonClick() {
        Mixer.prepareIosOutputElement();
        devicesHidden = false;
        Dispatcher.resumeAudioContextFirstTime();
        DTSInterface.resumeAudioContextFirstTime(self_type);
        _armInitWatchdog();
    }

    function onDirectJoinClick() {
        // -- Resume audio in the click gesture (required), then auto-join when ready (_auto_join reactive).
        // -- We deliberately do NOT reveal the device step -- there is nothing to show in no-conf here.
        _auto_join = true;
        Mixer.prepareIosOutputElement();
        Dispatcher.resumeAudioContextFirstTime();
        DTSInterface.resumeAudioContextFirstTime(self_type);
        _armInitWatchdog();
    }

    async function onDeviceSelect(type, value){

        if (OutputConfig.session.browser?.isIOS && type === "microphone" && /airpod|bluetooth/i.test(value))
            Mixer.kickIosOutputRoute();

        loading_media = true;

        if (type === "camera" || type === "microphone"){

            // -- the CameraPreview follows the selection stores on its own; nothing to do here
            await Devices.selectInputDevice(type, value).catch(e => console.error(e));
        }

        else await OutputDevices.select(type, value).catch(e => console.error(e));

        loading_media = false;
    }

    function onNameInput(e){
        Peers_Handler.notify_name_change(e.target.value);
    }

    // -- A <select value={...}> is only re-applied when the VALUE changes, and our selections
    // -- are named before the device list that holds them is published (Devices opens the
    // -- device, names the selection, and only then re-enumerates into avail_devices). The
    // -- select is left with nothing selected, and appending an option to a select in that
    // -- state makes the browser select the FIRST one -- "Mute" on the camera menu, the wrong
    // -- device on the others -- with nothing to correct it. So re-assert every value when the
    // -- option lists change, which is the only thing that can trigger that reset.
    //
    // -- Only then: OutputDevices.select() writes the speaker/stream selection through its
    // -- serialized chain (behind an in-flight setSinkId, that spans paints), so a sync on
    // -- every update would re-assert the old device over a pick the store hasn't caught up
    // -- to yet. A pick never changes the device list, so this can't fight one.
    let camera_select, mic_select, speaker_select, stream_select;
    let _synced_devices = null;

    afterUpdate(() => {

        if (_synced_devices === _avail_devices)
            return;

        _synced_devices = _avail_devices;

        _syncSelect(camera_select, _camera_muted ? Devices.MUTE_LABEL : _selected_devices.camera.label ?? "");
        _syncSelect(mic_select, _selected_devices.microphone.label ?? "");
        _syncSelect(speaker_select, _selected_devices.speaker.id ?? "");
        _syncSelect(stream_select, _selected_devices.stream.id ?? "");
    });

    // -- an unmatched value leaves the select blank (selectedIndex -1), which is the honest
    // -- state: the selection names a device this list doesn't hold (yet)
    function _syncSelect(select, value){
        if (select && select.value !== value)
            select.value = value;
    }

    // -- re-raises the browser prompt where the platform still allows one (a dismissed or
    // -- closed prompt); a hard block fails fast and the notice's settings hint applies
    function onRequestPermissions(){
        Logger.log("Start Panel Event", "info", "Clicked Allow Devices", {});
        Devices.requestPermissions().catch(e => console.error(e));
    }

    async function onJoinClick(){

        if (!can_join || joining) return;

        // -- non-independent roles don't own the stream pref (their live value just mirrors
        // -- the speaker) -- preserve whatever an eng session stored on this browser profile
        UserPrefs.writeNameAndSelectedDevices(name, { keep_stored_stream: !OutputDevices.streamIndependent() });
        Peers_Handler.notify_name_change(name);

        if (self_type === "eng"){
            Dispatcher.setEngConfig();
            _use_expanded_io = true; // just for now
        }

        Peers_Handler.set_can_render_tile(Peers_Handler.self_peer.id, true);

        // -- Conference.ok: a settings round-trip lands back here already joined
        const join_conference = !conf_disabled && !Conference.ok;

        // -- the built-in mic joins muted (no echo cancellation); they unmute from the toolbar.
        // -- Talent's mic goes over DTS, not the conference
        if (join_conference && self_type !== "talent" && builtin_mic && !getSelectedDeviceMuted("microphone"))
            Devices.toggleInputMute("microphone").catch(e => console.error(e));

        // -- keep every subsequent log line attributed to this person by name
        Logger.user_name = name;
        // -- mute + track state at the join moment: "joined but heard/said nothing" forensics
        Logger.log("Start Panel Event", "info", "Clicked Join", {
            name: name,
            mic_muted: getSelectedDeviceMuted("microphone"),
            cam_muted: getSelectedDeviceMuted("camera"),
            mic_track: !!Devices.tracks.audio.track,
            cam_track: !!Devices.tracks.video.track,
            builtin_mic: builtin_mic,
        })

        if (join_conference){

            joining = true;
            const result = await _joinConference();

            if (result.status !== "ok"){

            	// -- stay on the panel so the conference failure is visible rather than silent,
                // -- and leave the button live so it can be retried. The tracks published above
                // -- stay on the conference -- a retry skips what is already published and only
                // -- re-joins. (joining=false re-activates the CameraPreview on its own.)
                joining = false;
                joinBtnText = "Retry";
                console.error(`Conference Join Error -- SHOW UI HERE: ${result.info}`);
                return;
            }
        }

        // -- joining stays set on the way out: it holds the black + loader over the panel for
        // -- the length of App's fade off the session. Clearing it here flashes the panel back
        // -- for that whole fade. The component is destroyed at the end of it.
        dispatch("show_session");
    }

    // -- publish, THEN join -- lib-jitsi-meet wants the local tracks on the conference before
    // -- join() so they go out with the initial presence/offer instead of forcing a renegotiation
    // -- immediately after. See https://github.com/jitsi/ljm-jaas-example/blob/master/example.js
    async function _joinConference(){

        // -- the connection was made at page load; it may have dropped while the user sat here
        const connected = await Conference.ensureConnected();
        if (connected.status !== "ok") return connected;

        // -- talent publishes its audio over DTS, so it joins the conference bare
        if (self_type !== "talent"){
            await Devices.prepareConferenceTracks().catch(e => {
                // -- non-fatal: join anyway so the user can at least see and hear the room
                console.error(e);
                Logger.log("Conference", "error", "Publish Local Tracks Failed", {error: e?.message ?? String(e)});
            });
        }

        return await Conference.join();
    }

    onMount(() => {

        const saved_name = UserPrefs.prefs?.name ?? "";
        if (!get(Peers_Handler.self_peer.name))
            Peers_Handler.notify_name_change(saved_name);

        // -- pre-join funnel entry point: ~half of page loads never reach "Clicked Join" and
        // -- this is the first marker that the panel actually rendered for them.
        // -- returning=true means a settings round-trip remount, not a fresh arrival.
        Logger.log("Start Panel Event", "info", "Start Panel Shown", {
            saved_name: !!(UserPrefs.prefs?.name),
            devices_hidden: devicesHidden,
            returning: get(has_joined),
        });

    })

    onDestroy(() => {
        unsub_name();
        clearTimeout(_init_watchdog);
    });


    // ------------------------ //

    function _allowJoin (){
        joinBtnText = "Join";
        can_join = true;
    }

    function _disallowJoin (){
        joinBtnText = "Loading";
        can_join = false;
        //console.log("[PL-StartPanel] Allow Join New State: ", can_join);
    }


</script>

    <!-- on:change={(e) => toggleCamPreview(e.target.value)} -->
    <div id='sp-root' class:joining>

        <div id="top-wrapper">
            <CameraPreview hidden={devicesHidden} active={!joining}/>
        </div>

        <div id='middle-wrapper'>

            <input type='text' 
                id='name-input' 
                spellcheck='false' 
                autocomplete="off" 
                placeholder="Enter Name"
                on:input={onNameInput}
                bind:value={name}
            />
            
            {#if devicesHidden}
                {#if name !== ""}
                    {#if direct_join}
                        <button id='name-button' on:click={onDirectJoinClick}>Join</button>
                    {:else}
                        <button id='name-button' on:click={onNameButtonClick}>OK</button>
                    {/if}
                {/if}
            {:else}

                <div transition:fade style="width: 100%">
                    
                    {#if !conf_disabled}
                    <div class="conf-section">

                        <div class="section-heading">
                        	<h2>Conference</h2>
                            <!-- {#if self_type === "client" || _use_expanded_io}
                                <h2>Conference</h2>
                            {:else}
                                <h2>Conference + Stream</h2>
                            {/if} -->
                        </div>

                        {#if $device_permissions.microphone !== "granted"}
                            <div class="perm-notice" class:denied={$device_permissions.microphone === "denied"}>
                                {#if $device_permissions.microphone === "denied"}
                                    <h3>Device Permissions Required</h3>
                                    <p class="perm-copy">Joining needs your microphone.</p>
                                    <button id="perm-retry" on:click={onRequestPermissions}>Allow Devices</button>
                                    <p class="perm-hint">If no prompt appears, allow the microphone in your browser's site settings, then try again.</p>
                                {:else}
                                    <p class="perm-copy">Waiting for device permissions&hellip;</p>
                                    <p class="perm-hint">If no prompt appeared, check that a microphone is connected.</p>
                                {/if}
                            </div>
                        {/if}

                        {#if $device_permissions.microphone === "granted" || $device_permissions.camera === "granted"}
                        <div class="devicesWrapper">
                            {#if $device_permissions.camera === "granted"}
                                <i class="fa-thin fa-video"></i>
                                <select id="cameraSelect" bind:this={camera_select} value={_camera_muted ? Devices.MUTE_LABEL : $selected_devices.camera.label ?? ""} on:change={(e) => onDeviceSelect("camera", e.target.value)}>
                                    <option value={Devices.MUTE_LABEL}>{_camera_muted ? "Muted" : Devices.MUTE_LABEL}</option>
                                    {#each _avail_devices.camera as device}
                                        <option value={device.label}>{device.label}</option>
                                    {/each}
                                </select>
                            {/if}

                            {#if $device_permissions.microphone === "granted"}
                                
                                <i class="fa-thin fa-microphone"></i>
                                <select id="micSelect" bind:this={mic_select} value={$selected_devices.microphone.label ?? ""} on:change={(e) => onDeviceSelect("microphone", e.target.value)}>
                                    {#each _avail_devices.microphone as device}
                                        <option value={device.label}>{device.label}</option>
                                    {/each}
                                </select>
                    
                                {#if BrowserHandler.supports_setSpeakerId(OutputConfig.session.browser)}
                                    <i class="fa-thin fa-volume"></i>
                                    <select id="speakerSelect" bind:this={speaker_select} value={$selected_devices.speaker.id ?? ""} on:change={(e) => onDeviceSelect("speaker", e.target.value)}>
                                        {#each _avail_devices.speaker as device}
                                            <option value={device.deviceId}>{device.label}</option>
                                        {/each}
                                    </select>
                                {/if}
                               
                                {#if self_type === 'talent' && !BuildSettings.DISABLE_TALENT_EXPANDED_IO}
                                    <input type="checkbox" id="expanded-io" bind:checked={_use_expanded_io}>
                                    <label for="expanded-io" style="cursor: pointer;">
                                        <h6>Separate Stream i/o</h6>
                                    </label>
                                {/if}
                            {/if}
                        </div>
                        {/if}
                    </div>


                    {/if}

                    {#if !conf_disabled && (self_type === 'eng' || (self_type === "talent" && _use_expanded_io))}

                        <div transition:fade class="stream-section">
                            
                            <div class="section-heading">
                                <h2>Stream</h2>
                            </div>

                            <div class="devicesWrapper">
                                <h3>i/o</h3>
                                <select value={"Postlink Plugin"}>
                                    <option value={"Postlink Plugin"}>{"Postlink Plugin"}</option>
                                </select>
                                <h3>monitor</h3>
                                <select bind:this={stream_select} value={$selected_devices.stream.id ?? ""} on:change={(e) => onDeviceSelect("stream", e.target.value)}>
                                    {#each _avail_devices.speaker as device}
                                        <option value={device.deviceId}>{device.label}</option>
                                    {/each}
                                </select>
                            </div>
                        </div>
                        
                    {/if}


                    {#if self_type === 'eng'}
                        
                        <div transition:fade class="stream-section">
                            
                            <div class="section-heading">
                                <h2>Config</h2>
                            </div>

                            <div class="devicesWrapper">
                                
                                <!-- <input type="checkbox" id="force-vStream" bind:checked={OutputConfig.force_vStream} 
                                    title={force_vStream_hover}
                                >
                                <label for="force-vStream" style="cursor: pointer;"
                                    title={force_vStream_hover}
                                >
                                    <h6>ignore video caching</h6>
                                </label> -->
                                
                                <!-- <input type="checkbox" id="low_vStream_quality" bind:checked={OutputConfig.low_vStream_quality}
                                    title={low_vStream_quality_hover}
                                >
                                <label for="low_vStream_quality" style="cursor: pointer;"
                                    title={low_vStream_quality_hover}
                                >
                                    <h6>lower video stream quality</h6>
                                </label> -->

                                <!-- <div class="local-video-rendering-option">
                                    <h6>streaming video bitrate</h6>
                                    <div class="option-buttons">
                                        <label class:selected={video_bitrate === "hi"}>
                                            <input type="radio" name="video_bitrate" value="hi" 
                                                bind:group={video_bitrate}>
                                            hi
                                        </label>
                                        <label class:selected={video_bitrate === "low"}>
                                            <input type="radio" name="video_bitrate" value="low" 
                                                bind:group={video_bitrate}>
                                            low
                                        </label>
                                    </div>
                                </div> -->

                                <div class="local-video-rendering-option">
                                    <h6>local video rendering</h6>
                                    <div class="option-buttons">
                                        <label class:selected={local_video_rendering === "on"}>
                                            <input type="radio" name="local_video_rendering" value="on" 
                                                bind:group={local_video_rendering}>
                                            on
                                        </label>
                                        <label class:selected={local_video_rendering === "off"}>
                                            <input type="radio" name="local_video_rendering" value="off" 
                                                bind:group={local_video_rendering}>
                                            off
                                        </label>
                                    </div>
                                </div>

                                <!-- <input type="checkbox" id="override_disable_lcl_vrender" bind:checked={OutputConfig.override_disable_lcl_vrender}
                                    title={override_disable_lcl_vrender}
                                >
                                <label for="override_disable_lcl_vrender" style="cursor: pointer;"
                                    title={override_disable_lcl_vrender}
                                >
                                    <h6>ovveride disable local video rendering (now does nothing)</h6>
                                </label> -->
                                
                                <!-- <input type="checkbox" id="confvid_autoPause" bind:checked={OutputConfig.confvid_autoPause}
                                    title={cv_autoPause_hover}
                                >
                                <label for="confvid_autoPause" style="cursor: pointer;"
                                    title={cv_autoPause_hover}
                                >
                                    <h6>conference video auto pause</h6>
                                </label> -->
                            </div>
                        </div>
                        
                    {/if}


                    {#if builtin_mic}
                        <div class="headphone-notice">
                            <!-- <i class="fa-thin fa-headphones"></i> -->
                            <p>Headphones recommended. If you're joining without them, the speaker may cause echo when your mic is unmuted.</p>
                        </div>
                    {/if}

                    <button id="join-button" style="margin-top: 0.5em"
                            class:clickable={can_join && !joining}
                            class:notClickable={!can_join || joining}
                            on:click="{onJoinClick}">
                        {#if joining || !mic_permitted || $core_init_failed}
                            <!-- joining: this button is already behind the black overlay, and ITS loader
                                 is the one that shows. Blocked on permissions or a failed core init: the
                                 notice says why -- a spinner would read as progress. -->
                            <p id='joinBtnText'>Join</p>
                        {:else if can_join}
                            <p id='joinBtnText'>{joinBtnText}</p>
                        {:else}
                            <!-- not ready yet: the jitsi connection, the decoder thread, the audio context -->
                            <Loader/>
                        {/if}
                    </button>
                </div>
            {/if}

            <!-- -- outside the devices step: the direct-join flow never leaves the name step -->
            {#if $core_init_failed}
                <div class="perm-notice denied init-failed">
                    <h3>Couldn't Start The Session</h3>
                    <p class="perm-copy">Something went wrong while connecting.</p>
                    <button id="init-reload" on:click={onReloadClick}>Reload</button>
                </div>
            {/if}
        </div>
    </div>

    <!-- -- Joining: the panel steps aside in the same update and leaves a black screen with
         -- the loader -- no fade either way in, so the click lands straight on it. The fade UP
         -- on the session is the panel wrapper's own transition:fade in App.svelte, which takes
         -- this overlay with it (a local out: doesn't run when a parent block goes), so the
         -- black holds at full until the session is under it. Only a failed join fades this
         -- out on its own -- the panel is hidden rather than unmounted, so it comes back with
         -- its selects, its typed name and its camera preview intact. -->
    {#if joining}
        <div id="joining-overlay" out:fade={{ duration: 150 }}>
            <Loader/>
        </div>
    {/if}

    <!-- <div id="termsWrapper">
        <div/>
        <h4 id="terms">by joining you agree to our <a href="https://www.post-link.io/terms" target="_blank">Terms of Service</a> and <a href="https://www.post-link.io/privacy-policy" target="_blank">Privacy Policy</a>.</h4>
        <div/>
    </div> -->

<style lang="scss">

    @import "../styles/main.scss";

    .section-heading {
        
        margin-bottom: 5px;

        h2 {
            position: relative;
            background: rgb(10, 10, 10); // Match the app's background
            color: #97877e;
            font-size: 1.7rem;
            font-weight: 400;
            margin: 0;
            z-index: 1;
        }
    }

    .devicesWrapper {
        display: grid;
        grid-template-columns: 2em 1fr;
        row-gap: 1.1rem;
        column-gap: 0.8rem;
        font-family: inherit;
        font-size: 1em;
        padding: 1rem;
        border-radius: 10px;
        background: #2e2e2e66;
        
        h6 {
            font-size: 0.6em;
            color: #97877e;
            font-weight: 200;
            margin-left: 4px;
            cursor: pointer;
        }

        input[type="checkbox"] {
            appearance: none;
            -webkit-appearance: none;
            width: 16px;
            height: 16px;
            border: 1px solid rgba(190, 157, 134, 0.3);
            border-radius: 3px;
            background: transparent;
            cursor: pointer;
            position: relative;
            
            &:checked {
                background: rgba(190, 157, 134, 0.2);
                border-color: rgba(190, 157, 134, 0.4);
                
                &::after {
                    content: '✓';
                    position: absolute;
                    color: #be9d86;
                    font-size: 12px;
                    left: 2px;
                    top: -1px;
                }
            }

            &:hover {
                border-color: rgba(190, 157, 134, 0.5);
            }

            &:focus {
                outline: none;
                box-shadow: 0 0 0 2px rgba(190, 157, 134, 0.2);
            }
        }

        .local-video-rendering-option {
            grid-column: 1 / -1;
            display: flex;
            align-items: center;
            gap: 0.8em;
            margin-top: 0.3em;

            h6 {
                margin: 0;
                margin-left: 0;
                min-width: 10em;
            }

            .option-buttons {
                display: flex;
                gap: 0.3em;

                label {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.2em 0.6em;
                    font-size: 0.55em;
                    font-weight: 300;
                    color: #97877e;
                    border: 1px solid rgba(190, 157, 134, 0.3);
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    background: transparent;

                    input[type="radio"] {
                        display: none;
                    }

                    &:hover {
                        border-color: rgba(190, 157, 134, 0.5);
                        color: #be9d86;
                    }

                    &.selected {
                        background: rgba(190, 157, 134, 0.2);
                        border-color: rgba(190, 157, 134, 0.4);
                        color: #be9d86;
                    }
                }
            }
        }
    }

    .deviceWrapper{

        margin-top: 0.5em;
        margin-bottom: 0.5em;

    }

    .headphone-notice {

        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.6em;

        padding: 0.7rem 1rem;
        margin-top: 1.1rem;
        border-radius: 10px;

        // -- advisory amber: same tint construction as .perm-notice.denied, one step down in
        // -- urgency -- nothing is broken, the join still works without headphones
        background: rgba(214, 160, 30, 0.22);
        border: 1px solid rgba(255, 210, 120, 0.34);

        // -- paired with the commented-out icon in the markup
        // i {
        //     font-size: 0.7em;
        //     color: #ffdca8;
        // }

        p {
            margin: 0;
            font-size: 0.5em;
            font-weight: 250;
            color: #ffdca8;
            text-align: left;
            text-wrap: balance;
        }
    }

    .perm-notice {

        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.55em;

        padding: 1rem;
        margin-bottom: 1.1rem;
        border-radius: 10px;
        background: #2e2e2e66;

        h3 {
            margin: 0;
            font-size: 0.75em;
            font-weight: 400;
            color: #97877e;
        }

        .perm-copy {
            margin: 0;
            font-size: 0.55em;
            font-weight: 250;
            color: #97877e;
        }

        .perm-hint {
            margin: 0;
            font-size: 0.45em;
            font-weight: 250;
            color: #97877e99;
        }

        #perm-retry, #init-reload {
            width: auto;
            height: 2.2em;
            padding: 0 1.4em;
            font-size: 0.55em;
            font-weight: 400;
            color: #ffd5d5;
            background: #00a48a91;
            transition: background 0.18s ease;

            &:hover {
                background: #00887191;
            }
        }

        &.denied {
            background: rgba(122, 31, 31, 0.16);
            border: 1px solid rgba(255, 92, 92, 0.22);

            h3 {
                color: #ffb9b0;
            }
        }

        // -- a direct child of #middle-wrapper (centered flex column), not of a block section
        &.init-failed {
            align-self: stretch;
            margin: 1.1rem 0 0;
        }
    }


    .stream-section {

        margin-top: 2rem;
        .devicesWrapper {
            grid-template-columns: 2em 1fr;
            h3 {
                font-size: $font-xsm;
                color: #97877e;
                // margin-left: 1rem;
            }
        }
    }
    

    #sp-root {

        position: relative;

        display: grid;
        grid-template-columns: 1fr 1.5fr 1fr;
        grid-template-rows: 0.5fr 1fr;

        font-family: $font-family;
        font-size: 2.1rem;
        font-weight: 250;
        color: rgb(252, 249, 246);

        overflow-y: auto;
    }

    /* -- out of the way the instant Join is clicked -- no transition, so the black + loader
       -- is what the click lands on. visibility, not opacity, so the panel nobody can see is
       -- also out of the tab order and the accessibility tree; and not display:none, so the
       -- camera preview keeps its element and a failed join is revealed again under the
       -- overlay's own fade-out. (Same trick App.svelte plays on #session-wrapper.) */
    #sp-root.joining {
        visibility: hidden;
    }

    #joining-overlay {

        position: fixed;
        inset: 0;
        z-index: 1;

        /* -- nothing in here is interactive, and it outlives a failed join by the length of
           -- its fade -- don't let it eat the click that reaches the restored Retry button */
        pointer-events: none;

        display: flex;
        align-items: center;
        justify-content: center;

        background-color: rgb(10, 10, 10);
    }


    #top-wrapper {
        
        grid-column: 2/3;
        grid-row: 1/2;
        
        height: 35vh;
        min-height: 264px;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        
    }

    /* Name */
    #middle-wrapper{
        
        grid-column: 2/3;
        grid-row: 2/3;
        width: 55%;
        min-width: 20.5rem;
        justify-self: center;
        
        display: flex;
        align-items: center;
        flex-direction: column;
        /* justify-content: space-between; */
        gap: 10px;
    }


    #name-input {
        
        width: 100%;
        height: 1.5em;
        margin-top: 0.7em;
        margin-bottom: 0.3em;

        border-radius: 5px;
        
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: none;
        
        outline: solid #9e9e9e94;
        
        font-family: inherit;
        font-size: 2.1rem;
        font-weight: 300;

        color: #ffd5d5;
        background-color: #00000054;

        transition: all 0.3s ease;
    }

    /* #name:focus {
        box-shadow: 0 0 0 4px rgba(139, 131, 142, 0.4);
    }
    #name-ok:hover{
        background: #00887191;
    } */
    /* Buttons */

    button {

        display: inline-block;
        width: 100%;
        height: 2.5em;

        border: 0.05em solid rgba(255, 255, 255, 0.372);
        border-radius: 8px;

        background-color: rgb(10, 10, 10);

        font-family: inherit;
        font-size: 1.7rem;
        font-weight: 200;
        text-align: center;

        cursor: pointer;
    }

    #name-button{
        background: #00a48a91;
        font-weight: 400;
        color: #ffd5d5;
    }


    p {
        margin: 0px;
        text-align: center;
        color: #ffd5d5;
    }

    .notClickable {
        box-shadow: none;
        cursor: default;
    }

    .clickable{
        background: #00a48a91;
        transition: box-shadow 0.18s ease;
    }

    .clickable:hover {
        background: #00887191;
    }

    select {

        width: 100%;
        background: transparent;
        color: inherit;
        
        border: none;
        box-shadow: none;
        outline: none;

        font-family: inherit;
        font-size: 0.6em;
        font-weight: 300;

        /* Make selects render flat across browsers (Safari needs explicit reset) */
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-image: none;
        background-color: transparent;
        border-radius: 0;
    }

    select:hover{
        color: rgba(171, 168, 168, 0.987);
        cursor: pointer;
    }

    select:focus{
        box-shadow: 0 0 0 4px rgba(139, 131, 142, 0.4);
    }

    option {
        width: 100%;
    }

    .fa-thin {
        font-size: 1em;
        
    }

    /* Terms */
    /* #termsWrapper{
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    #terms {
        
        background: transparent;
            
        font-family: 'Inter', sans-serif;
        font-size: 0.7em;
        font-weight: 250;
        color: rgb(252, 249, 246);
    }

    a:-webkit-any-link{
        color:#fdc6be;
    } */


</style>
